import {
	IconCreditCard,
	IconGauge,
	IconUserCheck,
	IconUserPlus,
	IconUsers,
	IconUsersGroup,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useStaffGuard } from "@/components/admin/staff-guard";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useFetchAdminStats } from "@/components/queries/admin";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";

function StatCard({
	title,
	value,
	icon: Icon,
	tone,
}: {
	title: string;
	value: number | string;
	icon: React.ComponentType<{ className?: string }>;
	tone: string;
}) {
	return (
		<Card className="shadow-sm">
			<CardContent className="p-6">
				<div className="flex items-center gap-4">
					<div className={`p-2 rounded-lg ${tone}`}>
						<Icon className="h-5 w-5" />
					</div>
					<div>
						<p className="text-2xl font-bold">{value}</p>
						<p className="text-sm text-muted-foreground">{title}</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

export function AdminOverview() {
	const { isStaff, isLoading } = useStaffGuard();
	const { stats, isLoadingStats } = useFetchAdminStats();

	if (isLoading || isLoadingStats || !isStaff) {
		return <Loader />;
	}

	const plans = Object.entries(stats?.subscriptions_by_plan ?? {}).sort(
		(a, b) => b[1] - a[1],
	);
	const teamTypes = Object.entries(stats?.teams_by_type ?? {}).sort(
		(a, b) => b[1] - a[1],
	);

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Admin", href: null },
					{ title: "Overview", href: "/admin" },
				]}
				title="Admin Overview"
				description="Platform-wide totals across users, teams, and subscriptions"
				icon={<IconGauge className="size-6 text-primary" aria-hidden />}
			/>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				<StatCard
					title="Total Users"
					value={stats?.total_users ?? 0}
					icon={IconUsers}
					tone="bg-primary/10 text-primary"
				/>
				<StatCard
					title="Verified Users"
					value={stats?.verified_users ?? 0}
					icon={IconUserCheck}
					tone="bg-green-500/10 text-green-600"
				/>
				<StatCard
					title="New Users (30d)"
					value={stats?.new_users_last_30_days ?? 0}
					icon={IconUserPlus}
					tone="bg-blue-500/10 text-blue-600"
				/>
				<StatCard
					title="Active Subscriptions"
					value={stats?.active_subscriptions ?? 0}
					icon={IconCreditCard}
					tone="bg-orange-500/10 text-orange-600"
				/>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Card className="shadow-sm">
					<CardHeader className="pb-4">
						<CardTitle className="flex items-center gap-2">
							<IconCreditCard className="h-5 w-5" aria-hidden />
							Subscriptions by Plan
						</CardTitle>
						<CardDescription>
							Active billing records grouped by plan
						</CardDescription>
					</CardHeader>
					<CardContent className="pt-0">
						{plans.length === 0 && (
							<p className="text-sm text-muted-foreground">
								No subscriptions yet.
							</p>
						)}
						<div className="space-y-2">
							{plans.map(([plan, count]) => (
								<div
									key={plan}
									className="flex items-center justify-between rounded-lg border px-3 py-2"
								>
									<span className="font-medium">{plan}</span>
									<span className="text-muted-foreground">{count}</span>
								</div>
							))}
						</div>
					</CardContent>
				</Card>

				<Card className="shadow-sm">
					<CardHeader className="pb-4">
						<CardTitle className="flex items-center gap-2">
							<IconUsersGroup className="h-5 w-5" aria-hidden />
							Teams by Type
						</CardTitle>
						<CardDescription>
							{stats?.total_teams ?? 0} teams total ·{" "}
							<Link to="/admin/teams" className="text-primary hover:underline">
								Browse teams
							</Link>
						</CardDescription>
					</CardHeader>
					<CardContent className="pt-0">
						{teamTypes.length === 0 && (
							<p className="text-sm text-muted-foreground">No teams yet.</p>
						)}
						<div className="space-y-2">
							{teamTypes.map(([type, count]) => (
								<div
									key={type}
									className="flex items-center justify-between rounded-lg border px-3 py-2"
								>
									<span className="font-medium capitalize">{type}</span>
									<span className="text-muted-foreground">{count}</span>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			</div>
		</PageLayout>
	);
}

/* istanbul ignore start -- TanStack Router file route registration */
export const Route = createFileRoute("/_authorized/admin/")({
	component: AdminOverview,
});
/* istanbul ignore end */
